import ImgIADrawer from '@orientaction/Asset/Ressources/circle-chatbot-conseiller.png';
import clsx from 'clsx';
import { useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import RedesignImage from '../../components/RedesignImage';
import { IAChatMessage } from '../types';
import { useIAChatMessagesStyles } from './IAChatMessages.styles';
import TypingIndicator from './TypingIndicator';

type IAChatMessagesProps = {
  messages: IAChatMessage[];
  isLoading: boolean;
};

const formatTime = (date: Date) =>
  date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });

const IAChatMessages = ({ messages, isLoading }: IAChatMessagesProps) => {
  const classes = useIAChatMessagesStyles();
  const lastMessageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({ block: 'start' });
    }
  }, [messages, isLoading]);

  return (
    <div className={classes.messagesList}>
      {messages.map((message, index) => {
        const isLastMessage = index === messages.length - 1;

        return (
          <div
            key={message.id}
            className={classes.messageContainer}
            ref={isLastMessage ? lastMessageRef : null}>
            {message.sender === 'assistant' && (
              <div className={classes.messageHeader}>
                <RedesignImage
                  src={ImgIADrawer}
                  alt="Votre conseiller Orientaction"
                  className={classes.messageAvatar}
                />
                <span className={classes.messageSender}>Votre conseiller Orientaction</span>
                <span className={classes.messageSeparator}>•</span>
                <span className={classes.messageTime}>{formatTime(message.timestamp)}</span>
              </div>
            )}
            {message.sender === 'user' && (
              <div className={clsx(classes.messageHeader, classes.messageHeaderRight)}>
                <span className={classes.messageSender}>Vous</span>
                <span className={classes.messageSeparator}>•</span>
                <span className={classes.messageTime}>{formatTime(message.timestamp)}</span>
              </div>
            )}
            <div
              className={
                message.sender === 'user'
                  ? classes.userMessage
                  : clsx(classes.messageBubble, classes.assistantMessage)
              }>
              {message.sender === 'assistant' ? (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    img: ({ node, ...props }) => (
                      <RedesignImage
                        {...props}
                        crossOrigin="anonymous"
                        style={{
                          maxWidth: '100%',
                          height: 'auto',
                          borderRadius: 8,
                          marginTop: 12,
                          marginBottom: 12,
                        }}
                        alt={props.alt || 'Image'}
                        onError={e => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ),
                    a: ({ node, ...props }) => (
                      <a {...props} target="_blank" rel="noopener noreferrer">
                        {props.children}
                      </a>
                    ),
                  }}>
                  {message.text}
                </ReactMarkdown>
              ) : (
                message.text.split('\n').map((line, i, array) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <span key={`${message.id}-line-${i}`}>
                    {line}
                    {i < array.length - 1 && <br />}
                  </span>
                ))
              )}
            </div>
          </div>
        );
      })}
      {isLoading && <TypingIndicator />}
    </div>
  );
};

export default IAChatMessages;
